import React, { useState, useRef, useEffect } from 'react';
import { isDayAllowed } from '../../../utils/dateConstraints';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DAY_HEADERS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const parseISO = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
};

const toISO = (year, month, day) =>
  `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const DatePicker = ({
  value = '',
  onChange,
  placeholder = 'Select date',
  required,
  id,
  name,
  disabled,
  dateConstraint, // 'past' | 'future' | undefined — today and earlier/later only
}) => {
  const today = new Date();
  const parsed = parseISO(value);

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState((parsed || today).getFullYear());
  const [viewMonth, setViewMonth] = useState((parsed || today).getMonth());
  const [yearInput, setYearInput] = useState(false);
  const [yearDraft, setYearDraft] = useState(String((parsed || today).getFullYear()));

  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', handler);
    };
  }, [open]);

  // Sync view when value changes externally
  useEffect(() => {
    if (parsed) {
      setViewYear(parsed.getFullYear());
      setViewMonth(parsed.getMonth());
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayValue = parsed
    ? parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();

  const isSelected = (day) =>
    parsed &&
    parsed.getFullYear() === viewYear &&
    parsed.getMonth() === viewMonth &&
    parsed.getDate() === day;

  const isToday = (day) =>
    today.getFullYear() === viewYear &&
    today.getMonth() === viewMonth &&
    today.getDate() === day;

  const isDayDisabled = (day) => !isDayAllowed(dateConstraint, toISO(viewYear, viewMonth, day));

  const pickDay = (day) => {
    if (isDayDisabled(day)) return;
    onChange?.({ target: { value: toISO(viewYear, viewMonth, day) } });
    setOpen(false);
  };

  const pickToday = () => {
    onChange?.({ target: { value: toISO(today.getFullYear(), today.getMonth(), today.getDate()) } });
    setOpen(false);
  };

  const clearDate = (e) => {
    e.stopPropagation();
    onChange?.({ target: { value: '' } });
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const commitYearInput = () => {
    const y = parseInt(yearDraft, 10);
    if (!isNaN(y) && y >= 1900 && y <= 2100) setViewYear(y);
    setYearInput(false);
  };

  return (
    <div className="dp-wrap" ref={wrapRef}>
      {/* Trigger */}
      <div
        className={`dp-trigger${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`}
        onClick={() => !disabled && setOpen(o => !o)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={e => !disabled && e.key === 'Enter' && setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="dp-trigger-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </span>
        <span className={`dp-trigger-value${!displayValue ? ' is-placeholder' : ''}`}>
          {displayValue || placeholder}
        </span>
        {value && !disabled && (
          <button
            className="dp-clear-btn"
            type="button"
            onClick={clearDate}
            aria-label="Clear date"
            tabIndex={-1}
          >×</button>
        )}
        <span className={`dp-caret${open ? ' is-open' : ''}`}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </div>

      {/* Calendar popover */}
      {open && (
        <div className="dp-calendar" role="dialog" aria-label="Date picker">
          {/* Month/Year nav */}
          <div className="dp-nav">
            <button type="button" className="dp-nav-btn" onClick={prevMonth} aria-label="Previous month">‹</button>

            <div className="dp-nav-center">
              <span className="dp-nav-month">{MONTHS[viewMonth]}</span>
              {yearInput ? (
                <input
                  className="dp-year-input"
                  type="number"
                  value={yearDraft}
                  onChange={e => setYearDraft(e.target.value)}
                  onBlur={commitYearInput}
                  onKeyDown={e => { if (e.key === 'Enter') commitYearInput(); if (e.key === 'Escape') setYearInput(false); }}
                  autoFocus
                />
              ) : (
                <button
                  type="button"
                  className="dp-nav-year"
                  onClick={() => { setYearDraft(String(viewYear)); setYearInput(true); }}
                  title="Click to change year"
                >
                  {viewYear}
                </button>
              )}
            </div>

            <button type="button" className="dp-nav-btn" onClick={nextMonth} aria-label="Next month">›</button>
          </div>

          {/* Day headers */}
          <div className="dp-grid">
            {DAY_HEADERS.map(d => (
              <div key={d} className="dp-day-header">{d}</div>
            ))}

            {/* Empty cells for first week offset */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`e${i}`} />
            ))}

            {/* Day cells */}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
              <button
                key={day}
                type="button"
                disabled={isDayDisabled(day)}
                className={[
                  'dp-day',
                  isSelected(day) ? 'is-selected' : '',
                  isToday(day) && !isSelected(day) ? 'is-today' : '',
                  isDayDisabled(day) ? 'is-disabled' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => pickDay(day)}
                aria-label={`${MONTHS[viewMonth]} ${day}, ${viewYear}`}
                aria-pressed={!!isSelected(day)}
              >
                {day}
              </button>
            ))}
          </div>

          {/* Footer */}
          <div className="dp-footer">
            <button type="button" className="dp-today-btn" onClick={pickToday}>Today</button>
          </div>
        </div>
      )}

      {/* Hidden input for form submit / required attr */}
      <input type="hidden" id={id} name={name} value={value || ''} required={required} />
    </div>
  );
};

export default DatePicker;
